import { createStyles, makeStyles } from "@material-ui/core/styles";
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Toolbar,
} from "@mui/material";
import PropTypes from "prop-types";
import { Suspense, useCallback, useState } from "react";
import Iconify from "src/components/Iconify";
import { IconButtonAnimate } from "src/components/animate";
import Logo from "src/components/logo";
import Settings from "src/components/settings";
import { HEADER, WORDPRESS } from "src/config";
import useOffSetTop from "src/hooks/useOffSetTop";
import useResponsive from "src/hooks/useResponsive";
import { RootStyle } from "src/layouts/shared";
import AccountPopover from "./AccountPopover";
import NotificationsPopover from "./NotificationsPopover";
import CurrencyPopOver from "./components/currency-popover";
import LanguagePopover from "./language-popover";
import useAuth from "src/hooks/useAuth";
import { useSnackbar } from "notistack";
import Ternary from "src/components/ternary";
import buildPath from "src/utils/build-path";

DashboardHeader.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
};

export const useStyles = makeStyles(() =>
  createStyles({
    rotateIcon: {
      animation: "$spin 8s linear infinite",
    },
    "@keyframes spin": {
      "0%": {
        transform: "rotate(360deg)",
      },
      "100%": {
        transform: "rotate(0deg)",
      },
    },
  })
);

export default function DashboardHeader({
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false,
}) {
  const { enqueueSnackbar } = useSnackbar();

  const isOffset =
    useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;
  const isDesktop = useResponsive("up", "lg");

  const [openSettings, setOpenSettings] = useState(false);
  const { isAdmin, user } = useAuth();

  const handleOpenSettings = () => {
    setOpenSettings((prev) => !prev);
  };

  const handleCloseSettings = () => {
    setOpenSettings(false);
  };

  const classes = useStyles();
  const onClick = useCallback(() => {
    const textToCopy = `${window.origin}/${user.username}`;
    navigator.clipboard.writeText(textToCopy);
    enqueueSnackbar("Referral Copied");
  }, [WORDPRESS, user.username, enqueueSnackbar]);
  return (
    <>
      <RootStyle
        isCollapse={isCollapse}
        isOffset={isOffset}
        verticalLayout={verticalLayout}
      >
        <Toolbar
          sx={{
            backgroundColor: "background.paper",
            minHeight: "100% !important",
            px: { lg: 5 },
          }}
        >
          {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

          {!isDesktop && (
            <>
              <IconButtonAnimate
                onClick={onOpenSidebar}
                sx={{ mr: 1, color: "text.primary" }}
              >
                <Iconify icon="eva:menu-2-fill" />
              </IconButtonAnimate>
            </>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 0.5, sm: 1.5 }}
          >
            {/* {!isDesktop && <QuickPopover />} */}
            <Ternary
              when={isAdmin}
              then={
                <TextField
                  variant="outlined"
                  value={buildPath(window.origin, user.username)}
                  size="small"
                  sx={{
                    width: "250px",
                    color: "black",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" onClick={onClick} size="small">
                          <Iconify icon={"solar:copy-linear"} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    inputProps: {
                      style: {
                        color: "#454F64",
                      },
                      readOnly: true, // Optional: prevents editing
                    },
                  }}
                />
              }
            />

            <CurrencyPopOver />

            <NotificationsPopover />
            <Badge
              color="error"
              variant="dot"
              sx={{
                "& .MuiBadge-badge": {
                  top: "7px",
                  right: "7px",
                },
              }}
            >
              <IconButtonAnimate
                onClick={() => handleOpenSettings()}
                className={classes.rotateIcon}
              >
                <Iconify icon="solar:settings-bold-duotone" />
              </IconButtonAnimate>
            </Badge>

            <Suspense fallback={<CircularProgress />}>
              <LanguagePopover />
            </Suspense>
            <AccountPopover />
          </Stack>
        </Toolbar>
      </RootStyle>

      <Settings handleClose={handleCloseSettings} open={openSettings} />
    </>
  );
}
